import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { ProfileCard } from "@/components/perfis/ProfileCard";
import { ProfilePermissionsModal } from "@/components/perfis/ProfilePermissionsModal";
import { UsersPanel } from "@/components/perfis/UsersPanel";
import { UserKind, userKindOf } from "@/components/perfis/user-kind";
import { AssignProfileModal } from "@/components/perfis/AssignProfileModal";
import { supabase } from "@/integrations/supabase/client";
import type { UserProfile, UserWithProfile } from "@/types/user-profile";

interface Agencia {
  id: string;
  nome: string;
}

export default function GestaoPerfis() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<UserProfile | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithProfile | null>(null);
  const queryClient = useQueryClient();

  // Query para buscar perfis
  const {
    data: profiles = [],
    isLoading: isLoadingProfiles,
    isError: isErrorProfiles,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ["user_profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .order("id", { ascending: true });

      if (error) {
        console.error("Erro ao buscar perfis:", error);
        throw error;
      }

      return (data || []) as UserProfile[];
    },
  });

  // Query para buscar usuários com seus perfis (usando a view)
  const {
    data: users = [],
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["users_with_profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_profiles_with_access_profile")
        .select("*")
        .order("full_name", { ascending: true });

      if (error) {
        console.error("Erro ao buscar usuários:", error);
        throw error;
      }

      return (data || []) as UserWithProfile[];
    },
  });

  // Query para buscar agências
  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias_filter"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");

      if (error) {
        console.error("Erro ao buscar agências:", error);
        return [];
      }
      return (data || []) as Agencia[];
    },
  });

  // Filtragem de perfis
  const filteredProfiles = profiles.filter((profile) =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const internalUsers = users.filter((user) => userKindOf(user) === UserKind.Internal);
  const agencyUsers = users.filter((user) => userKindOf(user) === UserKind.Agency);

  const handleViewPermissions = (profile: UserProfile) => {
    setSelectedProfile(profile);
  };

  const handleChangeProfile = useCallback((user: UserWithProfile) => {
    setSelectedUser(user);
  }, []);

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["users_with_profiles"] });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestão de Perfis e Usuários"
        subtitle="Gerencie perfis de acesso e atribua permissões aos usuários"
      />

      <Tabs defaultValue="internal-users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profiles">Perfis</TabsTrigger>
          <TabsTrigger value="internal-users">Usuários Corisco ({internalUsers.length})</TabsTrigger>
          <TabsTrigger value="agency-users">Usuários de agência ({agencyUsers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="profiles" className="space-y-6">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar perfis..."
          />

          {isLoadingProfiles ? (
            <LoadingState variant="grid" />
          ) : isErrorProfiles ? (
            <ErrorState onRetry={() => refetchProfiles()} />
          ) : filteredProfiles.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Nenhum perfil encontrado"
              description={
                searchTerm
                  ? "Nenhum perfil corresponde a esse termo."
                  : "Nenhum perfil de acesso cadastrado ainda."
              }
            />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredProfiles.map((profile) => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  onViewPermissions={handleViewPermissions}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="internal-users">
          <UsersPanel
            kind={UserKind.Internal}
            users={internalUsers}
            profiles={profiles}
            agencias={agencias}
            isLoading={isLoadingUsers}
            isError={isErrorUsers}
            onRetry={() => refetchUsers()}
            onChangeProfile={handleChangeProfile}
            onUserCreated={handleSuccess}
          />
        </TabsContent>

        <TabsContent value="agency-users">
          <UsersPanel
            kind={UserKind.Agency}
            users={agencyUsers}
            profiles={profiles}
            agencias={agencias}
            isLoading={isLoadingUsers}
            isError={isErrorUsers}
            onRetry={() => refetchUsers()}
            onChangeProfile={handleChangeProfile}
            onUserCreated={handleSuccess}
          />
        </TabsContent>
      </Tabs>

      <ProfilePermissionsModal
        profile={selectedProfile}
        onClose={() => setSelectedProfile(null)}
      />

      <AssignProfileModal
        user={selectedUser}
        profiles={profiles}
        onClose={() => setSelectedUser(null)}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
